import { create } from 'zustand'
import fetcher from "../fetcher";
import {useListingsStore} from "./listingsStore";

// Refetch listings when the saved set changes while "My Dealers" is active, so
// the dealer filter stays in sync.
const refetchListingsIfFiltered = () => {
    const listings = useListingsStore.getState()
    if (listings.dealerMode === 'my') {
        listings.fetchListings()
        if (listings.viewMode === 'map') listings.fetchMapListings()
    }
}

// Per-user saved dealerships (keyed by the provider dealer id / external_id).
// Shared across the save toggles (cards, detail, map popup) and the ListingsPage
// saved-dealerships panel.
export const useSavedDealershipsStore = create((set, get) => ({
    saved: [],
    loaded: false,
    _inflight: null,

    // Fetch once. Concurrent callers (page mount + MainContent) share the same
    // in-flight request so it isn't fetched twice. Save/remove keep the list in
    // sync locally, so `loaded` stays true.
    fetchSaved: async (force = false) => {
        if (get().loaded && !force) return
        if (get()._inflight) return get()._inflight

        const promise = (async () => {
            const response = await fetcher.getSavedDealerships()
            if (response?.data) set({saved: response.data, loaded: true})
            set({_inflight: null})
        })()
        set({_inflight: promise})
        return promise
    },

    // Upsert a dealership. Accepts a frontend dealership object; the id (Visor
    // dealer_id) becomes external_id. Replaces any existing entry for that id.
    saveDealership: async (dealership) => {
        const externalId = String(dealership?.id ?? dealership?.external_id ?? '')
        if (!externalId) return

        const payload = {
            external_id: externalId,
            name: dealership.name || '',
            city: dealership.city || '',
            state: dealership.state || '',
            latitude: dealership.latitude ?? null,
            longitude: dealership.longitude ?? null,
            phone: dealership.phone || '',
            // Makes the dealership sells (from the search result). Used to collapse
            // dealers that don't carry the make being searched. Card/map saves have
            // no makes on their dealership object, so this can be empty.
            makes: Array.isArray(dealership.makes) ? dealership.makes : [],
        }

        const response = await fetcher.saveDealership(payload)
        if (response?.data) {
            const rest = get().saved.filter(d => String(d.external_id) !== externalId)
            set({saved: [...rest, response.data]})
            refetchListingsIfFiltered()
        }
    },

    // Remove by the SavedDealership row id (pk).
    removeDealership: async (savedId) => {
        await fetcher.deleteSavedDealership(savedId)
        set({saved: get().saved.filter(d => d.id !== savedId)})
        refetchListingsIfFiltered()
    },
}))
